import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faTimes, faPlus } from "@fortawesome/free-solid-svg-icons";

import { MeetdayModalFacade } from "../meetday-modal.facade";
import { DateTimePickerComponent } from "src/app/shared/components/datetime-picker/datetime-picker.component";

@Component({
  selector: "app-meetday-tab-rounds",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    FontAwesomeModule,
    DateTimePickerComponent,
  ],
  template: `
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-3">
      <div>
        <h3 class="mb-1">{{ 'Rounds' | translate }}</h3>
        <small class="text-muted">
          {{ 'Configure rounds and set their schedule. Limits are shared across rounds.' | translate }}
        </small>
      </div>
    </div>

    @if (facade.isGlobalRoundLimitValid()) {
    <div class="alert alert-secondary" role="status">
      {{ 'Current base limit per company per round' | translate }}:
      <strong>{{ facade.globalRoundLimit() }}</strong>.
      {{ 'Companies can override this limit individually on the Companies tab.' | translate }}
    </div>
    }

    <div>
      @if (rounds().length === 0) {
      <div class="alert alert-info">
        {{ 'No rounds yet. Click "Add Round" below to create one.' | translate }}
      </div>
      }

      @if (rounds().length > 0) {
      <table class="table table-striped table-sm w-100 align-middle">
        <thead>
          <tr>
            <th class="align-middle">{{ 'Round' | translate }}</th>
            <th class="align-middle">{{ 'Start time' | translate }}</th>
            <th class="align-middle">{{ 'End time' | translate }}</th>
            <th class="text-right align-middle" style="width: 100px;">{{ 'Actions' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (round of rounds(); track $index; let i = $index) {
          <tr [formGroup]="getRoundFormGroup(i)">
            <td class="align-middle font-weight-bold">
              {{ 'Round' | translate }} {{ i + 1 }}
            </td>
            <td class="align-middle">
              <app-datetime-picker formControlName="roundTimeStart" mode="time" />
            </td>
            <td class="align-middle">
              <app-datetime-picker formControlName="roundTimeEnd" mode="time" />
            </td>
            <td class="text-right text-nowrap align-middle">
              <button class="btn btn-sm btn-outline-danger" type="button" (click)="facade.removeRound(i)" [title]="'Delete' | translate">
                <fa-icon [icon]="faTimes"></fa-icon>
              </button>
            </td>
          </tr>
          }
        </tbody>
      </table>
      }

      <button class="btn btn-primary mt-3" type="button" (click)="facade.addRound()">
        <fa-icon [icon]="faPlus"></fa-icon>
        {{ 'Add Round' | translate }}
      </button>
    </div>
  `,
})
export class MeetdayTabRoundsComponent {
  facade = inject(MeetdayModalFacade);

  // Icons
  faTimes = faTimes;
  faPlus = faPlus;

  // Local computed
  rounds = computed(() => this.facade.roundsFormArray.controls);

  // Helpers
  getRoundFormGroup(index: number): FormGroup {
    return this.facade.roundsFormArray.at(index) as FormGroup;
  }

  isRoundInvalid(index: number): boolean {
    const round = this.getRoundFormGroup(index);
    return !!(round && round.invalid && (round.dirty || round.touched));
  }
}
